import React, { useState, useEffect, useRef } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import './Speakers.css';
import profile from '../images/profile2.png';
import company from '../images/company.png';
import flag from '../images/flag.avif';
import { speakersService } from '../services/speakersService';
import { supabase } from '../supabaseClient';

function SpeakersOnline() {
  const [speakersData, setSpeakersData] = useState({
    onlineSpeakersHeader: { title: 'Intervenants En Ligne' },
    onlineSpeakersIntro: { text: '' },
    onlineSpeakersSection: { title: 'Conférenciers Online' },
    onlineSpeakers: []
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [speakersPerSlide, setSpeakersPerSlide] = useState(4);
  const [expanded, setExpanded] = useState({});
  const infoRefs = useRef([]);
  const [maxInfoHeight, setMaxInfoHeight] = useState(0);
  const [animationDirection, setAnimationDirection] = useState(null);
  const [displayedSlide, setDisplayedSlide] = useState(0);
  const [animationState, setAnimationState] = useState('idle'); // 'idle' | 'fade-out' | 'fade-in'
  const [pendingSlide, setPendingSlide] = useState(null);

  useEffect(() => {
    setDisplayedSlide(currentSlide);
  }, [currentSlide]);

  useEffect(() => {
    // Responsive speakers per slide
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setSpeakersPerSlide(4);
      } else {
        setSpeakersPerSlide(4);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalSlides = Math.ceil(speakersData.onlineSpeakers.length / speakersPerSlide);

  useEffect(() => {
    loadSpeakersData();
  }, []);

  const loadSpeakersData = async () => {
    try {
      setLoading(true);
      console.log('Loading online speakers data...');
      
      // Get active edition first
      const { data: activeEdition, error: editionError } = await supabase
        .from('editions')
        .select('year')
        .eq('is_active', true)
        .single();
      
      let editionYear = '2025'; // Default fallback
      
      if (editionError) {
        console.warn('No active edition found, using fallback:', editionYear);
      } else if (activeEdition) {
        editionYear = activeEdition.year;
        console.log('Active edition found:', editionYear);
      }

      const data = await speakersService.getSpeakersData(editionYear);
      console.log('Online speakers service returned:', data);
      
      if (data) {
        setSpeakersData({
          onlineSpeakersHeader: data.onlineSpeakersHeader,
          onlineSpeakersIntro: data.onlineSpeakersIntro,
          onlineSpeakersSection: data.onlineSpeakersSection,
          onlineSpeakers: data.onlineSpeakers
        });
        console.log('Online speakers data set:', data);
      } else {
        // Use default data if none found
        console.warn('No online speakers data found, using defaults');
      }
      setError(null);
    } catch (error) {
      console.error('Error loading online speakers data:', error);
      setError('Failed to load online speakers data');
    } finally {
      setLoading(false);
      console.log('Online speakers loading completed');
    }
  };

  const nextSlide = () => {
    if (animationState !== 'idle') return;
    const next = (currentSlide + 1) % totalSlides;
    setPendingSlide(next);
    setAnimationState('fade-out');
  };

  const prevSlide = () => {
    if (animationState !== 'idle') return;
    const prev = (currentSlide - 1 + totalSlides) % totalSlides;
    setPendingSlide(prev);
    setAnimationState('fade-out');
  };

  useEffect(() => {
    if (animationState === 'fade-out') {
      const timeout = setTimeout(() => {
        setCurrentSlide(pendingSlide);
        setAnimationState('fade-in');
      }, 300);
      return () => clearTimeout(timeout);
    }
    if (animationState === 'fade-in') {
      const timeout = setTimeout(() => {
        setAnimationState('idle');
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [animationState, pendingSlide]);

  const getCurrentSpeakers = () => {
    // Only show speakers for the displayed slide
    const start = displayedSlide * speakersPerSlide;
    return speakersData.onlineSpeakers.slice(start, start + speakersPerSlide);
  };

  // Update max height for info boxes on slide change or speakers change
  useEffect(() => {
    if (!infoRefs.current) return;
    const heights = infoRefs.current.map(ref => ref ? ref.offsetHeight : 0);
    setMaxInfoHeight(Math.max(...heights, 0));
  }, [currentSlide, speakersData.onlineSpeakers, speakersPerSlide]);

  // Toggle expand/collapse for a speaker
  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Helper to truncate text and add "Voir plus/moins"
  const renderExpandableText = (text, id, maxLength = 60) => {
    if (!text) return null;
    if (text.length <= maxLength) return <span>{text}</span>;
    const isOpen = expanded[id];
    return (
      <>
        <span>
          {isOpen ? text : text.slice(0, maxLength) + '...'}
        </span>
        <button
          className="voir-plus-btn"
          onClick={() => toggleExpand(id)}
          type="button"
        >
          {isOpen ? 'Voir moins' : 'Voir plus'}
        </button>
      </>
    );
  };

  if (loading) {
    return null; // Don't show loading for online speakers section
  }

  // Don't render if no online speakers
  if (speakersData.onlineSpeakers.length === 0) {
    return null;
  }

  return (
    <div className="speakers-page">
      <div className="speakers-container">
        <div className="speakers-header">
          <h1>{speakersData.onlineSpeakersHeader.title}</h1>
        </div>
        
        <div className="speakers-content">
          <div className="speakers-overview">
            <div className="speakers-intro">
              <p className="intro-text">
                {speakersData.onlineSpeakersIntro.text}
              </p>
            </div>

            <div className="congress-speakers">
              <h2>{speakersData.onlineSpeakersSection.title}</h2>
              <div className="speakers-carousel-container">
                <div className="speakers-carousel">
                  <div
                    className={
                      "speakers-grid" +
                      (animationState === 'fade-out'
                        ? ' fade-out'
                        : animationState === 'fade-in'
                        ? ' fade-in'
                        : '')
                    }
                    key={displayedSlide}
                  >
                    {getCurrentSpeakers().map((speaker, idx) => (
                      <div key={speaker.id} className="speaker-member">
                        <div className="member-photo">
{speaker.companyLogo && (
  <div className="company-logo">
    <img 
      src={speaker.companyLogo} 
      alt="Company Logo" 
      onError={(e) => (e.target.style.display = "none")} 
    />
  </div>
)}


                          <img src={speaker.image || profile} alt={speaker.name} />
                          {/* <div className="country-flag">
                            <img src={speaker.flagUrl } />
                          </div> */}
                          <div className="online-tag">
                            <span>ONLINE</span>
                          </div>
                        </div>
                        <div
                          className="member-infospeaker"
                          ref={el => infoRefs.current[idx] = el}
                          style={{ minHeight: maxInfoHeight || undefined }}
                        >
                          <h4>{speaker.name}</h4>
                          <p>
                            {renderExpandableText(speaker.title, speaker.id + '-title')}
                          </p>
                          <p>
                            {renderExpandableText(speaker.organization, speaker.id + '-org')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {totalSlides > 1 && (
                <div className="speakers-carousel-controls">
                  <button className="speakers-carousel-btn prev" onClick={prevSlide}>
                    <HiChevronLeft />
                  </button>
                  <div className="speakers-carousel-indicators">
                    {Array.from({ length: totalSlides }, (_, index) => (
                      <button
                        key={index}
                        className={`speakers-indicator ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => setCurrentSlide(index)}
                      />
                    ))}
                  </div>
                  <button className="speakers-carousel-btn next" onClick={nextSlide}>
                    <HiChevronRight />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SpeakersOnline;
